import { useState, useCallback, useEffect } from 'react';
import { Camera, MapPin, Fingerprint, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { CameraCapture } from '../identity/CameraCapture';

interface SignatureEvidence {
  biometricImage: string;
  biometricHash: string;
  geolocation?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  deviceFingerprint: string;
  ipAddress: string;
  userAgent: string;
  timestamp: number;
}

interface SignatureCaptureProps {
  contractId: string;
  signerName: string;
  signerDocument: string;
  onSign: (evidence: SignatureEvidence) => void;
  onCancel: () => void;
  className?: string;
}

// Generate device fingerprint (simplified version)
async function generateDeviceFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform,
  ];

  const data = components.join('|');
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate hash from image data
async function generateImageHash(imageData: string): Promise<string> {
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(imageData));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export function SignatureCapture({
  contractId,
  signerName,
  signerDocument,
  onSign,
  onCancel,
  className = '',
}: SignatureCaptureProps) {
  const [step, setStep] = useState<'consent' | 'biometric' | 'location' | 'confirm'>('consent');
  const [biometricImage, setBiometricImage] = useState<string | null>(null);
  const [biometricHash, setBiometricHash] = useState<string | null>(null);
  const [geolocation, setGeolocation] = useState<SignatureEvidence['geolocation'] | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // Generate device fingerprint on mount
  useEffect(() => {
    generateDeviceFingerprint().then(setDeviceFingerprint);
  }, []);

  const handleBiometricCapture = useCallback(async (imageData: string) => {
    setBiometricImage(imageData);
    const hash = await generateImageHash(imageData);
    setBiometricHash(hash);
    setStep('location');
  }, []);

  const requestGeolocation = useCallback(() => {
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada pelo navegador');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeolocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setStep('confirm');
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Permissão de localização negada');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Localização indisponível');
            break;
          case error.TIMEOUT:
            setLocationError('Tempo esgotado ao obter localização');
            break;
          default:
            setLocationError('Erro ao obter localização');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  const handleSkipLocation = () => {
    setStep('confirm');
  };

  const handleConfirmSignature = async () => {
    if (!biometricImage || !biometricHash || !deviceFingerprint) return;

    setIsProcessing(true);

    try {
      const evidence: SignatureEvidence = {
        biometricImage,
        biometricHash,
        geolocation: geolocation || undefined,
        deviceFingerprint,
        ipAddress: '', // Will be captured by backend
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
      };

      onSign(evidence);
    } catch (error) {
      console.error('Error signing:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-blue-600 text-white px-6 py-4">
        <h2 className="text-lg font-semibold">Assinatura Eletrônica</h2>
        <p className="text-blue-100 text-sm">Contrato #{contractId.slice(0, 8)}</p>
      </div>

      <div className="p-6">
        {/* Signer Info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">Signatário</p>
          <p className="font-medium">{signerName}</p>
          <p className="text-sm text-gray-600">{signerDocument}</p>
        </div>

        {/* Step: Consent */}
        {step === 'consent' && (
          <div>
            <div className="mb-6">
              <h3 className="font-medium mb-4">Termos de Assinatura Eletrônica</h3>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600 max-h-48 overflow-y-auto">
                <p className="mb-2">
                  Ao prosseguir com a assinatura eletrônica, você declara que:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Leu e compreendeu o conteúdo do contrato</li>
                  <li>Concorda com todos os termos e condições</li>
                  <li>Autoriza a captura de dados biométricos para validação</li>
                  <li>Autoriza a captura de geolocalização (opcional)</li>
                  <li>Reconhece que esta assinatura tem validade jurídica</li>
                </ul>
              </div>
            </div>

            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">
                Li e concordo com os termos acima e autorizo a coleta dos dados necessários para a assinatura eletrônica.
              </span>
            </label>

            <div className="flex gap-4">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => setStep('biometric')}
                disabled={!consentChecked}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {/* Step: Biometric Capture */}
        {step === 'biometric' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Camera className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-medium">Captura Biométrica</h3>
                <p className="text-sm text-gray-500">Tire uma foto do seu rosto para validação</p>
              </div>
            </div>

            <CameraCapture
              onCapture={handleBiometricCapture}
              facingMode="user"
              showPreview={true}
            />
          </div>
        )}

        {/* Step: Location */}
        {step === 'location' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-medium">Geolocalização</h3>
                <p className="text-sm text-gray-500">Registrar localização da assinatura (opcional)</p>
              </div>
            </div>

            {geolocation ? (
              <div className="p-4 bg-green-50 rounded-lg mb-6">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Localização capturada</span>
                </div>
                <p className="text-sm text-green-600">
                  Lat: {geolocation.latitude.toFixed(6)}, Long: {geolocation.longitude.toFixed(6)}
                </p>
                <p className="text-xs text-green-500">
                  Precisão: ±{geolocation.accuracy.toFixed(0)}m
                </p>
              </div>
            ) : locationError ? (
              <div className="p-4 bg-yellow-50 rounded-lg mb-6">
                <div className="flex items-center gap-2 text-yellow-700 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">{locationError}</span>
                </div>
                <p className="text-sm text-yellow-600">
                  Você pode continuar sem a localização ou tentar novamente.
                </p>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg mb-6 text-center">
                <p className="text-gray-600 mb-4">
                  A geolocalização ajuda a validar a autenticidade da assinatura.
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleSkipLocation}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Pular
              </button>
              <button
                onClick={requestGeolocation}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {geolocation ? 'Continuar' : 'Permitir Localização'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Confirm */}
        {step === 'confirm' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Fingerprint className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-medium">Confirmar Assinatura</h3>
                <p className="text-sm text-gray-500">Revise os dados antes de assinar</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              {/* Biometric preview */}
              {biometricImage && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <img
                    src={biometricImage}
                    alt="Biometria"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Biometria capturada
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      Hash: {biometricHash?.slice(0, 16)}...
                    </p>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <MapPin className={`w-8 h-8 ${geolocation ? 'text-green-600' : 'text-gray-400'}`} />
                <div>
                  {geolocation ? (
                    <>
                      <p className="font-medium text-green-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Localização registrada
                      </p>
                      <p className="text-xs text-gray-500">
                        {geolocation.latitude.toFixed(4)}, {geolocation.longitude.toFixed(4)}
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-500">Localização não capturada</p>
                  )}
                </div>
              </div>

              {/* Device */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Fingerprint className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-medium text-green-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Dispositivo identificado
                  </p>
                  <p className="text-xs text-gray-500 font-mono">
                    ID: {deviceFingerprint?.slice(0, 16)}...
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Atenção:</strong> Ao clicar em "Assinar", você confirma que leu e concorda com todos os termos do contrato. Esta ação não pode ser desfeita.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={onCancel}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSignature}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Assinando...
                  </>
                ) : (
                  'Assinar Contrato'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
